import React, { useState, useCallback, useEffect } from "react";
import PersonWorkplacesComponent from "./PersonWorkplacesComponent";
import { PERSON_WORKPLACES_QUERY, PROFESSIONS_QUERY, POSITIONS_QUERY, EMPLOYERS_QUERY } from "./queries";
import { ADD_UPDATE_PERSON_WORKPLACES } from "./mutations";
import { useQuery, useMutation } from "@apollo/client";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { clone } from "ramda";
import { useDirtyFieldValidation } from "@totalsoft/pure-validations-react";
import { validatorPersonWorkplaces } from "./validators";
import { useToast } from "@totalsoft/rocket-ui";
import { getInt } from "utils/functions";
import { useSimpleError } from "hooks/apolloWrappers";

const PersonWorkplacesContainer = ({ partnerId, hasWritePermission }) => {
  const { t } = useTranslation();
  const showToast = useToast();
  const showError = useSimpleError();
  const [personWorkplacesState, setPersonWorkplacesState] = useState([]);
  const [saveDisabled, setSaveDisabled] = useState(true);

  const [validationPersonWorkplaces, validatePersonWorkplaces] = useDirtyFieldValidation(validatorPersonWorkplaces);

  useEffect(() => {
    validatePersonWorkplaces(personWorkplacesState);
  }, [personWorkplacesState, validatePersonWorkplaces]);

  const {
    data: personWorkplaces,
    loading: loadingPersonWorkplaces,
    refetch: refetchPeronWorkplaces
  } = useQuery(PERSON_WORKPLACES_QUERY, {
    variables: { partnerId },
    skip: !getInt(partnerId),
    onCompleted: data => {
      setPersonWorkplacesState(data.personWorkplaces);
    }
  });

  const { data: professions, loading: loadingProfessions } = useQuery(PROFESSIONS_QUERY);

  const { data: positions, loading: loadingPositions } = useQuery(POSITIONS_QUERY);

  const { data: employers, loading: loadingEmployers } = useQuery(EMPLOYERS_QUERY);

  const [addOrUpdatePersonWorkplaces] = useMutation(ADD_UPDATE_PERSON_WORKPLACES, {
    onError: error => {
      showError(error);
      setSaveDisabled(false);
    },
    onCompleted: _ => {
      showToast(t("PersonWorkplaces.Updated"), "success", { autoClose: 3000 });
      refetchPeronWorkplaces();
    }
  });

  const onAdd = useCallback(() => {
    setPersonWorkplacesState(state => [
      ...state,
      { personWorkplaceId: null, professionId: null, positionId: null, employerId: null, dateFrom: null, dateTo: null }
    ]);
  }, []);

  const onModify = useCallback(
    (event, field, idx) => {
      const localPersonWorkplacesState = clone(personWorkplacesState);
      localPersonWorkplacesState[idx][field] = event;
      setPersonWorkplacesState(localPersonWorkplacesState);
      setSaveDisabled(false);
    },
    [personWorkplacesState]
  );

  const onSave = useCallback(() => {
    addOrUpdatePersonWorkplaces({
      variables: { input: { partnerId: partnerId, personWorkplaces: personWorkplacesState } }
    });
    setSaveDisabled(true);
  }, [addOrUpdatePersonWorkplaces, partnerId, personWorkplacesState]);

  const onDelete = useCallback(
    idx => {
      const localPersonWorkplacesState = clone(personWorkplacesState);
      localPersonWorkplacesState.splice(idx, 1);
      setPersonWorkplacesState(localPersonWorkplacesState);
      setSaveDisabled(false);
    },
    [personWorkplacesState]
  );

  const loadedData = () =>
    !loadingPersonWorkplaces &&
    personWorkplaces &&
    !loadingProfessions &&
    professions &&
    !loadingPositions &&
    positions &&
    !loadingEmployers &&
    employers;

  return (
    loadedData && (
      <PersonWorkplacesComponent
        personWorkplaces={personWorkplacesState}
        professionData={professions?.professions}
        positions={positions?.positions}
        employers={employers?.employers}
        hasWritePermission={hasWritePermission}
        saveDisabled={saveDisabled}
        onAdd={onAdd}
        onModify={onModify}
        onSave={onSave}
        onDelete={onDelete}
        validationPersonWorkplaces={validationPersonWorkplaces}
      />
    )
  );
};
PersonWorkplacesContainer.propTypes = {
  partnerId: PropTypes.number.isRequired,
  hasWritePermission: PropTypes.bool.isRequired
};
export default PersonWorkplacesContainer;
